import {useAuthStore} from '@features/user/store/store.ts'
import {useTranslation, TKey} from '@/translations/i18n.tsx'
import {Assignment, ServiceType} from '@features/assignment/types.ts'
import {Button} from '@components/ui/button.tsx'
import {useState} from 'react'
import {ScheduleDateModal} from '@features/assignment/components/schedule-date-modal'
import {Spinner} from '@components/ui/spinner.tsx'
import {useAssignmentCoordination} from '@features/assignment/queries/useAssignmentCoordination.ts'
import {
    formatHostAvailabilityDates,
    isPastInListingTimeZone,
    sortHostAvailabilityDates
} from '@features/assignment/utils.tsx'
import {parseDate} from '@/dates.ts'
import {
    ASSIGNMENT_DESCRIPTION_PARAGRAPH_CLASSNAME,
    ASSIGNMENT_DESCRIPTION_TEXT_CLASSNAME,
    ASSIGNMENT_DESCRIPTION_TITLE_CLASSNAME,
    ASSIGNMENT_DESCRIPTION_WRAPPER_CLASSNAME
} from '@features/assignment/constants'

export const AcceptedDatesSharedDescription = ({
    assignment,
    serviceType
}: {
    assignment: Assignment
    serviceType: ServiceType
}) => {
    const {t} = useTranslation()
    const adminAccessToken = useAuthStore(state => state.adminAccessToken)
    const dates = sortHostAvailabilityDates(assignment.quote?.host_availability_dates ?? [])
    const [selectedDate, setSelectedDate] = useState<string | null>(null)
    const assignmentManualCoordinationMutation = useAssignmentCoordination({
        assignmentId: assignment.id,
        taskId: assignment.task.id
    })

    return (
        <div className={ASSIGNMENT_DESCRIPTION_WRAPPER_CLASSNAME}>
            <div className={ASSIGNMENT_DESCRIPTION_TEXT_CLASSNAME}>
                <h3 className={ASSIGNMENT_DESCRIPTION_TITLE_CLASSNAME}>
                    {t('assignment:descriptions:accepted_dates_shared:title')}
                </h3>
                <p className={ASSIGNMENT_DESCRIPTION_PARAGRAPH_CLASSNAME}>
                    {t(`assignment:descriptions:accepted_dates_shared:paragraph:${serviceType}` as TKey, {
                        count: dates.length,
                        dateCount: dates.length
                    })}
                </p>
            </div>
            <div className="flex flex-col items-center gap-2.5">
                <div className="grid w-full grid-cols-1 gap-2 [&>*:only-child]:col-span-full [&>*:only-child]:w-full [&>*:only-child]:justify-self-center md:w-auto md:grid-cols-2">
                    {dates.map(date => (
                        <Button
                            key={date.starting_time}
                            variant="outlinedPrimary"
                            disabled={
                                !date.starting_time ||
                                assignmentManualCoordinationMutation.isPending ||
                                (!adminAccessToken &&
                                    isPastInListingTimeZone(parseDate(date.starting_time), assignment.entity.timezone))
                            }
                            onClick={() => date.starting_time && setSelectedDate(date.starting_time)}
                        >
                            {formatHostAvailabilityDates(date.starting_time, date.ending_time)}
                        </Button>
                    ))}
                </div>
                <Button
                    variant="tertiary"
                    className="w-full md:w-auto"
                    onClick={() => assignmentManualCoordinationMutation.mutate({status: 'manual_coordination'})}
                    disabled={assignmentManualCoordinationMutation.isPending}
                >
                    {assignmentManualCoordinationMutation.isPending ? (
                        <Spinner size={20} />
                    ) : (
                        t('assignment:descriptions:accepted_dates_shared:reject_cta')
                    )}
                </Button>
            </div>

            {!!selectedDate && (
                <ScheduleDateModal
                    selectedDate={selectedDate}
                    assignmentId={assignment.id}
                    onClose={() => setSelectedDate(null)}
                />
            )}
        </div>
    )
}
